import React, {
	Dispatch,
	FC,
	RefObject,
	SetStateAction,
	useContext,
	useRef,
	useState
} from 'react'
import {
	AppContextProps,
	AppStateContext
} from '@components/layouts/DynamicLayout'
import TextAreaInput from '@components/requestForms/inputs/textarea.inputs'
import config from '@utils/config'
import { RequestInputs, RequestInputFiles, RequestNames } from '@utils/request'
import Modal from '../../modal'
import * as pageRequestDetailsUtils from '@pages/auth/requests/__[id].utils'
import { Request } from '@services/models'
import { Comment } from '@services/models/comments.model'
import { CustomDate } from '@services/models/shared.model'
import { ICommentsAttachment } from '@services/types'
import Loader from '../../loader'
import { requestCategories, requestSubCategories } from '@services/constants'
import BlobUploader from '@components/requestForms/inputs/azureBlobUploader.input'
import Notification from '@components/ui/notification'

type props = {
	request: Request
	isOpen: boolean
	addDocumentsType?: boolean
	setModal: () => void
	onInformationAdded: (string) => void
	setRequestCode?: Dispatch<SetStateAction<string | undefined>>
	onSetInputFiles?: (input: RequestInputFiles[]) => void
}

const NUMBER_OF_MAX_FILES = 19

const AddInformationModal: FC<props> = ({
	setModal,
	request,
	isOpen,
	onInformationAdded,
	setRequestCode
}) => {
	const { pageData, authUser } = useContext<AppContextProps>(AppStateContext)
	const [errors, setErrors] = useState<string[]>([])
	const [hasFileError, setHasFileError] = useState<boolean>(false)
	const [files, setFiles] = useState<Set<File>>(new Set<File>())
	const [joinedFile, setJoinedFiles] = useState<RequestInputFiles>({
		name: 'joinedFile',
		label: pageData?.assets?.page_requestInfo_enclosed,
		labelKey: 'request_form_clothDiapers_ninthQuestion_label',
		description: pageData?.assets?.page_requestInfo_enclosed,
		files: new Set<File>(),
		required: false,
		numberFilesRequired: 0,
		category: requestCategories.urbanisme,
		subCategory: requestSubCategories.autresSiRequis,
		uploadedFilesMap: new Map<string, string>(new Map())
	})
	const setUploadedFilesMap = (map: Map<string, string>) => {
		setJoinedFiles({
			...joinedFile,
			uploadedFilesMap: map
		})
	}

	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [comment, setComment] = useState<RequestInputs>({
		name: 'newConsent',
		value: '',
		label: pageData?.assets?.myRequest_new_information_related_to_the_request,
		required: true,
		labelKey: request?.typeId,
		ref: useRef<HTMLTextAreaElement>(null)
	})

	const onSelectFileJoinedFiles = (files: Set<File>) => {
		setJoinedFiles({
			...joinedFile,
			files
		})
	}

	const MAX_CHAR_DESC = config.request.input.textareaMaxChar

	const onFixError = (listErrors: string[]) => setErrors(listErrors)

	const onChangeComment = (value: string) => {
		setComment({ ...comment, value })
		if (comment.required) {
			onFixError(errors.filter((error) => error !== comment.name))
		}
	}

	const clearData = () => {
		setComment({ ...comment, value: '' })
		setFiles(new Set<File>())
		setErrors([])
		setHasFileError(false)
	}

	const processingDataAndValidation = () => {
		const listErrors: string[] = []

		if (comment.required && comment.value === '') {
			listErrors.push(comment.name)

			if (comment.ref) {
				comment.ref.current?.focus()
			}
		}

		setErrors(listErrors)

		if (listErrors.length === 0) {
			submitPost()
		}
	}

	const onSubmit = (processingData: () => void, hasError: boolean) => {
		if (hasError) {
			return false
		}

		processingData()
	}

	const submitPost = async () => {
		if (authUser) {
			setIsLoading(true)
			const rawFiles = [
				...files,
				...(joinedFile.files ? [...joinedFile.files] : [])
			]

			let attachment: Promise<ICommentsAttachment>[] | ICommentsAttachment[] =
				!!rawFiles.length
					? rawFiles.map(
							async (file): Promise<ICommentsAttachment> => ({
								portalCommentId: '',
								id: '',
								fileSize: file.size,
								dateAdd: new CustomDate(
									new Date(file.lastModified).toUTCString()
								),
								mimeType: file.type,
								fileName: file.name,
								document_reference: joinedFile.uploadedFilesMap
									? joinedFile.uploadedFilesMap.get(file.name)
									: undefined
							})
					  )
					: []

			Promise.all(attachment).then(async (result) => {
				const req = new Comment({
					portalMessageRead: false,
					requestId: request.id!,
					comment: comment.value,
					dateAdd: new CustomDate(new Date().toUTCString()),
					subject: comment.label,
					attachment: result as unknown as ICommentsAttachment[],
					files: rawFiles
				})

				setRequestCode && setRequestCode(request.code)
				const res = await req.saveAsync(false)
				setIsLoading(false)
				onInformationAdded(res)
				try {
					localStorage.setItem('addInformationSuccess', 'true')
				} catch (err) {
					// ignore storage errors
				}
				// reload page
				setTimeout(() => window.location.reload(), 3000)
			})
		}
	}

	const getMaxCharsDesc = () => {
		if (request?.name !== RequestNames.othersRequests) {
			return MAX_CHAR_DESC
		} else {
			return config.request.input.textareaMaxCharForOtherRequest
		}
	}

	return (
		<Modal
			title={`${pageData?.assets?.label_add_information_to_request} <br/> ${
				request?.code
			} <br /> ${pageRequestDetailsUtils.getRequestTitle(
				pageData.requests,
				request
			)}`}
			primaryButtonText={pageData?.assets?.label_send}
			cancelButtonText={pageData?.assets?.cancel}
			showExitBtn
			isOpen={isOpen}
			onCancelBtn={() => {
				setModal()
				clearData()
			}}
			onSetIsOpen={() => {}}
			onPrimaryButton={() =>
				onSubmit(processingDataAndValidation, errors.length > 0)
			}
			disabled={isLoading}
			shouldNotClose={isLoading}
		>
			{isLoading ? (
				<Loader text={pageData?.assets?.loading} />
			) : (
				<div className={pageRequestDetailsUtils.classes.modalContent}>
					<form>

						<Notification
							type="warning"
							text={
								"<b>Avertissement!</b> <br /> Veillez à soumettre l'intégralité de vos documents en un envoi unique. Suite à la soumission de votre demande, aucune pièce additionnelle ne pourra être jointe à votre demande jusqu'à la prise en charge effective de votre dossier."
							}
							hasHtml
						/>

						<fieldset>
							<TextAreaInput
								id={comment.name}
								label={comment.label}
								value={comment.value}
								maxCharacters={getMaxCharsDesc()}
								onChange={onChangeComment}
								required={comment.required}
								hasError={errors.includes(comment.name)}
								hasDescriptionLabel={`${getMaxCharsDesc()} ${
									pageData?.assets?.myRequest_characters_max
								}`}
								descriptionAlignment="left"
								ref={comment.ref as RefObject<HTMLTextAreaElement>}
							/>
						</fieldset>
						<fieldset>
							<h3 style={{ marginTop: '0' }}>
								{pageData?.assets?.page_requestInfo_enclosed}
							</h3>

							<BlobUploader
								name={pageData?.assets?.page_requestInfo_enclosed}
								description={
									pageData?.assets?.page_requestInfo_inputFileDesc_urba
								}
								required={false}
								disabled={false}
								files={joinedFile.files}
								uploadedFilesMap={joinedFile.uploadedFilesMap}
								hasError={hasFileError}
								onSetFiles={onSelectFileJoinedFiles}
								setUploadedFilesMap={setUploadedFilesMap}
								maxFiles={NUMBER_OF_MAX_FILES}
								onSetHasError={setHasFileError}
								isSummary={false}
								toPrint={false}
								totalFilesCount={files.size + (joinedFile.files?.size ?? 0)}
							/>
						</fieldset>

					</form>
				</div>
			)}
		</Modal>
	)
}

export default AddInformationModal
